import { MultiSelect } from "primereact/multiselect";
import React from "react";
import { connect, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import type { ThunkDispatch } from "redux-thunk";

import { accountsListActions } from "../../reducers/accountsList";
import { filterAccountActions } from "../../reducers/filterAccount";
import { siteNotiActions } from "../../reducers/siteNoti";
import { ClearFilterIcon } from "../SVGIcons";

interface FilterItem {
  value: string;
  label: string;
  isChecked: boolean;
}

interface AccountListActionBarState {
  searchKeyword: string;
  corporateNames: FilterItem[];
  storeNames: FilterItem[];
  registrationRoutes: FilterItem[];
  planTypes: FilterItem[];
  isActives: FilterItem[];
}

function mapStateToProps(state: {
  accountsList: unknown;
  filterAccount: AccountListActionBarState;
}) {
  return { ...state.accountsList, ...state.filterAccount };
}

interface AccountListActionBarProps {
  searchKeyword: string;
  corporateNames: FilterItem[];
  storeNames: FilterItem[];
  registrationRoutes: FilterItem[];
  planTypes: FilterItem[];
  isActives: FilterItem[];
}

const AccountListActionBar: React.FC<AccountListActionBarProps> = ({
  searchKeyword,
  corporateNames,
  storeNames,
  registrationRoutes,
  planTypes,
  isActives,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<ThunkDispatch<unknown, unknown, unknown>>();
  const selectedCorporateNames = corporateNames
    .filter((item) => item.isChecked)
    .map((item) => item.value);
  const selectedStoreNames = storeNames
    .filter((item) => item.isChecked)
    .map((item) => item.value);
  const selectedRegistrationRoutes = registrationRoutes
    .filter((item) => item.isChecked)
    .map((item) => item.value);
  const selectedPlanTypes = planTypes
    .filter((item) => item.isChecked)
    .map((item) => item.value);
  const selectedIsActives = isActives
    .filter((item) => item.isChecked)
    .map((item) => item.value);

  const switchCorporateNameChecked = (newValue: string[]) => {
    const newCheckItems = corporateNames.filter((item) =>
      newValue.includes(item.value),
    );
    dispatch(filterAccountActions.switchCorporateNameChecked(newCheckItems));
  };
  const switchStoreNameChecked = (newValue: string[]) => {
    const newCheckItems = storeNames.filter((item) =>
      newValue.includes(item.value),
    );
    dispatch(filterAccountActions.switchStoreNameChecked(newCheckItems));
  };
  const switchRegistrationRouteChecked = (newValue: string[]) => {
    const newCheckItems = registrationRoutes.filter((item) =>
      newValue.includes(item.value),
    );
    dispatch(
      filterAccountActions.switchRegistrationRouteChecked(newCheckItems),
    );
  };
  const switchPlanTypeChecked = (newValue: string[]) => {
    const newCheckItems = planTypes.filter((item) =>
      newValue.includes(item.value),
    );
    dispatch(filterAccountActions.switchPlanTypeChecked(newCheckItems));
  };
  const switchIsActiveChecked = (newValue: string[]) => {
    const newCheckItems = isActives.filter((item) =>
      newValue.includes(item.value),
    );
    dispatch(filterAccountActions.switchIsActiveChecked(newCheckItems));
  };
  const doSearch = () => {
    dispatch(filterAccountActions.applyFilter());
    dispatch(accountsListActions.doSearchKeywordAndFetch());
  };
  const clearSearch = () => {
    dispatch(accountsListActions.setSearchKeyword(""));
    dispatch(filterAccountActions.setInitState());
    dispatch(filterAccountActions.getFilters());
    dispatch(filterAccountActions.applyFilter());
    dispatch(accountsListActions.doSearchKeywordAndFetch());
  };

  const goToCreateAccount = () => {
    navigate("/account-create");
  };

  const showSiteNotiEdit = () => {
    dispatch(siteNotiActions.setIsPopupShow(true));
    dispatch(siteNotiActions.getSiteNoti());
  };

  return (
    <div className="flex sticky top-14 bg-white z-30 min-h-12 text-[#2d2d2d] py-2">
      <div className="flex items-center max-w-8xl w-full mx-auto px-4 sm:px-6 md:px-8">
        <form className="w-auto mr-3">
          <div className="flex">
            <div className="grid grid-cols-9 gap-2 w-full">
              <div className="col-span-3 flex items-center gap-2">
                <div className="flex items-center w-full">
                  <input
                    type="text"
                    value={searchKeyword}
                    onChange={(e) =>
                      dispatch(
                        accountsListActions.setSearchKeyword(e.target.value),
                      )
                    }
                    className="block w-full p-2 text-gray-900 border border-gray-300 rounded bg-gray-50 text-xs focus:ring-blue-500 focus:border-blue-500 min-w-full"
                    placeholder="法人名、店舗名、ユーザーID、メール"
                  />
                </div>
              </div>
              <div className="col-span-3 flex items-center gap-2">
                <span className="text-xs flex-shrink-0 min-w-16 text-right font-bold">
                  法人名
                </span>
                <div className="grid ml-auto w-full">
                  <MultiSelect
                    value={selectedCorporateNames}
                    onChange={(e) => switchCorporateNameChecked(e.value)}
                    options={corporateNames}
                    optionLabel="label"
                    filter
                    placeholder="（全て選択）"
                    className="appearance-none row-start-1 col-start-1 text-gray-900 border border-gray-300 rounded bg-gray-50 text-xs truncate focus:ring-blue-500 focus:border-blue-500 w-[144px] max-w-[144px] h-[2.125rem]"
                  />
                </div>
              </div>
              <div className="col-span-3 flex items-center gap-2">
                <span className="text-xs flex-shrink-0 min-w-16 text-right font-bold">
                  店舗名
                </span>
                <div className="grid w-full">
                  <MultiSelect
                    value={selectedStoreNames}
                    onChange={(e) => switchStoreNameChecked(e.value)}
                    options={storeNames}
                    optionLabel="label"
                    filter
                    placeholder="（全て選択）"
                    className="appearance-none row-start-1 col-start-1 text-gray-900 border border-gray-300 rounded bg-gray-50 text-xs truncate focus:ring-blue-500 focus:border-blue-500 w-[144px] max-w-[144px] h-[2.125rem]"
                  />
                </div>
              </div>
              <div className="col-span-3 flex items-center gap-2">
                <span className="text-xs flex-shrink-0 min-w-16 text-right font-bold">
                  ステータス
                </span>
                <div className="grid w-full">
                  <MultiSelect
                    value={selectedIsActives}
                    onChange={(e) => switchIsActiveChecked(e.value)}
                    options={isActives}
                    optionLabel="label"
                    filter
                    placeholder="（全て選択）"
                    className="appearance-none row-start-1 col-start-1 text-gray-900 border border-gray-300 rounded bg-gray-50 text-xs truncate focus:ring-blue-500 focus:border-blue-500 min-w-full h-[2.125rem]"
                  />
                </div>
              </div>
              <div className="col-span-3 flex items-center gap-2">
                <span className="text-xs flex-shrink-0 min-w-16 text-right font-bold">
                  利用プラン
                </span>
                <div className="grid w-full">
                  <MultiSelect
                    value={selectedPlanTypes}
                    onChange={(e) => switchPlanTypeChecked(e.value)}
                    options={planTypes}
                    optionLabel="label"
                    filter
                    placeholder="（全て選択）"
                    className="appearance-none row-start-1 col-start-1 text-gray-900 border border-gray-300 rounded bg-gray-50 text-xs truncate focus:ring-blue-500 focus:border-blue-500 w-[144px] max-w-[144px] h-[2.125rem]"
                  />
                </div>
              </div>
              <div className="col-span-3 flex items-center gap-2">
                <span className="text-xs flex-shrink-0 min-w-16 text-right font-bold">
                  登録経路
                </span>
                <div className="grid w-full">
                  <MultiSelect
                    value={selectedRegistrationRoutes}
                    onChange={(e) => switchRegistrationRouteChecked(e.value)}
                    options={registrationRoutes}
                    optionLabel="label"
                    filter
                    placeholder="（全て選択）"
                    className="appearance-none row-start-1 col-start-1 text-gray-900 border border-gray-300 rounded bg-gray-50 text-xs truncate focus:ring-blue-500 focus:border-blue-500 w-[144px] max-w-[144px] h-[2.125rem]"
                  />
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-2 w-[9.2rem] ml-3">
              <button
                type="button"
                onClick={doSearch}
                className="py-2 px-5 text-xs text-white bg-blue-600 border border-blue-600 rounded-lg hover:bg-blue-700 hover:text-white whitespace-nowrap"
              >
                検索
              </button>
              <button
                type="button"
                onClick={clearSearch}
                className="py-2 px-3 text-xs font-medium focus:outline-none bg-white rounded-lg border border-gray-300 hover:bg-gray-100 focus:z-10 focus:ring-4 focus:ring-gray-100 flex items-center gap-1"
              >
                <ClearFilterIcon />
                条件をクリア
              </button>
            </div>
          </div>
        </form>

        <div className="ml-auto flex gap-2 self-start">
          <button
            type="button"
            onClick={showSiteNotiEdit}
            className="py-2 px-5 text-sm font-medium text-white bg-blue-600 border border-blue-600 hover:bg-blue-700 hover:text-white rounded-md whitespace-nowrap"
          >
            お知らせ編集
          </button>
          <button
            type="button"
            onClick={goToCreateAccount}
            className="py-2 px-5 text-sm font-medium text-white bg-orange-500 hover:bg-orange-400 rounded-md whitespace-nowrap"
          >
            新規追加
          </button>
        </div>
      </div>
    </div>
  );
};

export default connect(mapStateToProps)(AccountListActionBar);
